import type { PersistStorage, StorageValue } from 'zustand/middleware';

const FLUSH_DELAY_MS = 500;

type AnyState = unknown;

interface PendingWrite {
  value: string;
}

const pending = new Map<string, PendingWrite>();
let scheduled = false;
let flushHandle: number | null = null;

const flushAll = () => {
  scheduled = false;
  flushHandle = null;
  for (const [key, { value }] of pending) {
    try {
      window.localStorage.setItem(key, value);
    } catch (err) {
      console.warn('[throttledStorage] setItem failed', key, err);
    }
  }
  pending.clear();
};

const scheduleFlush = () => {
  if (scheduled) return;
  scheduled = true;
  const ric = (window as any).requestIdleCallback as
    | ((cb: () => void, opts?: { timeout?: number }) => number)
    | undefined;
  if (ric) {
    flushHandle = ric(flushAll, { timeout: FLUSH_DELAY_MS });
  } else {
    flushHandle = window.setTimeout(flushAll, FLUSH_DELAY_MS);
  }
};

if (typeof window !== 'undefined') {
  const flushOnExit = () => {
    if (flushHandle != null) {
      const cic = (window as any).cancelIdleCallback as ((id: number) => void) | undefined;
      if (cic) cic(flushHandle);
      else clearTimeout(flushHandle);
      flushHandle = null;
    }
    flushAll();
  };
  window.addEventListener('pagehide', flushOnExit);
  window.addEventListener('beforeunload', flushOnExit);
}

export const createThrottledJSONStorage = <T = AnyState>(): PersistStorage<T> => ({
  getItem: (name) => {
    const buffered = pending.get(name)?.value;
    const raw = buffered ?? window.localStorage.getItem(name);
    if (raw == null) return null;
    try {
      return JSON.parse(raw) as StorageValue<T>;
    } catch (err) {
      console.warn('[throttledStorage] getItem parse failed', name, err);
      return null;
    }
  },
  setItem: (name, value) => {
    try {
      pending.set(name, { value: JSON.stringify(value) });
      scheduleFlush();
    } catch (err) {
      console.warn('[throttledStorage] setItem serialize failed', name, err);
    }
  },
  removeItem: (name) => {
    pending.delete(name);
    try {
      window.localStorage.removeItem(name);
    } catch (err) {
      console.warn('[throttledStorage] removeItem failed', name, err);
    }
  },
});
